// Manual Booking — hotel entry form (SMB-3541).
// Controlled: parent owns the HotelBookingForm state; updates flow back via onChange.
import React from 'react';
import { Box, RadioGroup, FormControlLabel, Radio, Divider } from '@mui/material';
import {
  HotelBookingForm,
  HotelForm,
  GuestForm,
  FieldErrors,
  PAX_TYPES,
  TITLES,
  BOARD_TYPES,
  emptyHotel,
  emptyGuest,
} from './types';
import { Field, SelectField, CheckField, RowCard, AddButton, SectionTitle, SubTitle, errorText } from './fields';

interface Props {
  form: HotelBookingForm;
  onChange: (next: HotelBookingForm) => void;
  errors: FieldErrors;
}

const opt = (v: string, l?: string) => ({ value: v, label: l ?? v });

const ManualHotelForm: React.FC<Props> = ({ form, onChange, errors }) => {
  const update = (patch: Partial<HotelBookingForm>) => onChange({ ...form, ...patch });

  const setAmount = (k: keyof HotelBookingForm['amounts'], v: string) =>
    update({ amounts: { ...form.amounts, [k]: v } });

  // ---- hotels ----
  const setHotels = (hotels: HotelForm[]) => update({ hotels });
  const patchHotel = (hi: number, patch: Partial<HotelForm>) =>
    setHotels(form.hotels.map((h, i) => (i === hi ? { ...h, ...patch } : h)));
  const addHotel = () => setHotels([...form.hotels, emptyHotel()]);
  const removeHotel = (hi: number) => setHotels(form.hotels.filter((_, i) => i !== hi));

  // ---- guests ----
  const setGuests = (guests: GuestForm[]) => update({ guests });
  const patchGuest = (gi: number, patch: Partial<GuestForm>) =>
    setGuests(form.guests.map((g, i) => (i === gi ? { ...g, ...patch } : g)));
  const setLead = (gi: number) =>
    setGuests(form.guests.map((g, i) => ({ ...g, leadPax: i === gi })));
  const addGuest = () => setGuests([...form.guests, emptyGuest(false)]);
  const removeGuest = (gi: number) => setGuests(form.guests.filter((_, i) => i !== gi));

  return (
    <Box>
      {/* ------------------------------------------------ Amounts */}
      <SectionTitle>Amounts (what the customer paid)</SectionTitle>
      <Box className="feeInputFieldWrap">
        <Field label="Total Paid" type="number" required value={form.amounts.totalUsrAmt}
          onChange={(v) => setAmount('totalUsrAmt', v)} error={errors['amounts.totalUsrAmt']} />
        <Field label="Supplier Total" type="number" required value={form.amounts.supTotalAmt}
          onChange={(v) => setAmount('supTotalAmt', v)} error={errors['amounts.supTotalAmt']} />
        <Field label="USD Total" type="number" required value={form.amounts.usCurTotalAmt}
          onChange={(v) => setAmount('usCurTotalAmt', v)} error={errors['amounts.usCurTotalAmt']} />
        <Field label="Currency" required value={form.amounts.currency}
          onChange={(v) => setAmount('currency', v)} error={errors['amounts.currency']} placeholder="USD" />
        <Field label="Hotel Price (optional)" type="number" value={form.amounts.productPrice}
          onChange={(v) => setAmount('productPrice', v)} error={errors['amounts.productPrice']} placeholder="defaults to Total Paid" />
      </Box>

      <Divider sx={{ my: 2 }} />

      {/* ------------------------------------------------ Hotels */}
      <SectionTitle>Hotels</SectionTitle>
      {form.hotels.map((h, hi) => (
        <RowCard
          key={hi}
          title={`Hotel ${hi + 1}`}
          onRemove={() => removeHotel(hi)}
          removeDisabled={form.hotels.length === 1}
        >
          <Box className="feeInputFieldWrap">
            <Field label="Supplier" required value={h.supplier}
              onChange={(v) => patchHotel(hi, { supplier: v })} error={errors[`hotels.${hi}.supplier`]} placeholder="NUITEE" />
            <Field label="Hotel Name" required value={h.hotelName}
              onChange={(v) => patchHotel(hi, { hotelName: v })} error={errors[`hotels.${hi}.hotelName`]} />
            <Field label="City" required value={h.city}
              onChange={(v) => patchHotel(hi, { city: v })} error={errors[`hotels.${hi}.city`]} />
            <Field label="Country" required value={h.country}
              onChange={(v) => patchHotel(hi, { country: v })} error={errors[`hotels.${hi}.country`]} placeholder="US" />
            <Field label="Check-in" type="date" required value={h.checkIn}
              onChange={(v) => patchHotel(hi, { checkIn: v })} error={errors[`hotels.${hi}.checkIn`]} />
            <Field label="Check-out" type="date" required value={h.checkOut}
              onChange={(v) => patchHotel(hi, { checkOut: v })} error={errors[`hotels.${hi}.checkOut`]} />
            <Field label="Confirmation Ref" required value={h.bookingReference}
              onChange={(v) => patchHotel(hi, { bookingReference: v })} error={errors[`hotels.${hi}.bookingReference`]} />
            <Field label="Room Name" required value={h.roomName}
              onChange={(v) => patchHotel(hi, { roomName: v })} error={errors[`hotels.${hi}.roomName`]} placeholder="Deluxe King" />
            <Field label="Room ID" value={h.roomId}
              onChange={(v) => patchHotel(hi, { roomId: v })} />
            <Field label="No. of Rooms" type="number" required min={1} value={h.noOfRooms}
              onChange={(v) => patchHotel(hi, { noOfRooms: v })} error={errors[`hotels.${hi}.noOfRooms`]} />
            <Field label="Adults" type="number" value={h.adultCount}
              onChange={(v) => patchHotel(hi, { adultCount: v })} />
            <Field label="Children" type="number" value={h.childCount}
              onChange={(v) => patchHotel(hi, { childCount: v })} />
            <SelectField label="Board Type" value={h.boardType}
              onChange={(v) => patchHotel(hi, { boardType: v as HotelForm['boardType'] })}
              options={BOARD_TYPES.map((b) => ({ value: b.value, label: b.label }))} />
            <Field label="Supplier Base Fare" type="number" required value={h.supBaseFare}
              onChange={(v) => patchHotel(hi, { supBaseFare: v })} error={errors[`hotels.${hi}.supBaseFare`]} />
            <Field label="Supplier Taxes" type="number" required value={h.supTaxes}
              onChange={(v) => patchHotel(hi, { supTaxes: v })} error={errors[`hotels.${hi}.supTaxes`]} />
            <Field label="Supplier Total Fare" type="number" required value={h.supTotalFare}
              onChange={(v) => patchHotel(hi, { supTotalFare: v })} error={errors[`hotels.${hi}.supTotalFare`]} />
            <Field label="Markup Amount" type="number" value={h.markupAmount}
              onChange={(v) => patchHotel(hi, { markupAmount: v })} />
            <Field label="Service Fee Amount" type="number" value={h.servicefeeAmount}
              onChange={(v) => patchHotel(hi, { servicefeeAmount: v })} />
            <Field label="Rooms Occupancy" value={h.roomsOccupancy}
              onChange={(v) => patchHotel(hi, { roomsOccupancy: v })} placeholder="1R-2A-0C" />
          </Box>
          <Box mt={1}>
            <CheckField label="Refundable" checked={h.isRefundable}
              onChange={(v) => patchHotel(hi, { isRefundable: v })} />
          </Box>
          <div className="form-control inputFieldWrap" style={{ marginTop: 8 }}>
            <label>Cancellation Policy (HTML allowed)</label>
            <textarea
              className="inputField"
              style={{ minHeight: 60, resize: 'vertical' }}
              value={h.cancellationPolicy}
              onChange={(e) => patchHotel(hi, { cancellationPolicy: e.target.value })}
              placeholder="<p>Free cancellation until …</p>"
            />
          </div>
        </RowCard>
      ))}
      <AddButton label="Add hotel" onClick={addHotel} />

      <Divider sx={{ my: 2 }} />

      {/* ------------------------------------------------ Guests */}
      <SectionTitle>Guests</SectionTitle>
      {errors._leadPax && <span style={errorText}>{errors._leadPax}</span>}
      {form.guests.map((g, gi) => (
        <RowCard
          key={gi}
          title={`Guest ${gi + 1}${g.leadPax ? ' (lead)' : ''}`}
          onRemove={() => removeGuest(gi)}
          removeDisabled={form.guests.length === 1}
        >
          <RadioGroup row value={g.leadPax ? 'lead' : ''} onChange={() => setLead(gi)}>
            <FormControlLabel
              value="lead"
              control={<Radio sx={{ color: '#272727', '&.Mui-checked': { color: '#272727' } }} />}
              label="Lead guest"
            />
          </RadioGroup>
          <Box className="feeInputFieldWrap">
            <SelectField label="Pax Type" value={g.paxType}
              onChange={(v) => patchGuest(gi, { paxType: v as GuestForm['paxType'] })}
              options={PAX_TYPES.map((t) => opt(t))} />
            <SelectField label="Title" required value={g.title}
              onChange={(v) => patchGuest(gi, { title: v })}
              options={[opt('', 'Select'), ...TITLES.map((t) => opt(t))]} error={errors[`guests.${gi}.title`]} />
            <Field label="First Name" required value={g.firstName}
              onChange={(v) => patchGuest(gi, { firstName: v })} error={errors[`guests.${gi}.firstName`]} />
            <Field label="Middle Name" value={g.middleName}
              onChange={(v) => patchGuest(gi, { middleName: v })} />
            <Field label="Last Name" required value={g.lastName}
              onChange={(v) => patchGuest(gi, { lastName: v })} error={errors[`guests.${gi}.lastName`]} />
            <Field label="Room No" required value={g.roomNo}
              onChange={(v) => patchGuest(gi, { roomNo: v })} error={errors[`guests.${gi}.roomNo`]} placeholder="1" />
            <Field label="Country Name" value={g.countryName}
              onChange={(v) => patchGuest(gi, { countryName: v })} />
            <Field label="ISO" value={g.iso}
              onChange={(v) => patchGuest(gi, { iso: v })} placeholder="US" />
          </Box>
          {g.leadPax && (
            <>
              <SubTitle>Lead contact</SubTitle>
              <Box className="feeInputFieldWrap">
                <Field label="Email" required value={g.email}
                  onChange={(v) => patchGuest(gi, { email: v })} error={errors[`guests.${gi}.email`]} />
                <Field label="Country Code" required value={g.countryCode}
                  onChange={(v) => patchGuest(gi, { countryCode: v })} error={errors[`guests.${gi}.countryCode`]} placeholder="1" />
                <Field label="Phone Number" required value={g.phoneNumber}
                  onChange={(v) => patchGuest(gi, { phoneNumber: v })} error={errors[`guests.${gi}.phoneNumber`]} />
              </Box>
            </>
          )}
        </RowCard>
      ))}
      <AddButton label="Add guest" onClick={addGuest} />
    </Box>
  );
};

export default ManualHotelForm;
